import cities from "../cities"
import {
	age,
	bool,
	city,
	degreeField,
	degreeName,
	disability,
	email,
	gender,
	hearAbout,
	jobType,
	level,
	maritalStatus,
	name,
	neighborhood,
	organizationSector,
	organizationSize,
	password,
	phone,
	previousExperience,
	turnstile_token,
	username,
} from "./_"
import { requiredImage } from "./edits"
import {
	ORGANIZATION_SECTOR,
	ORGANIZATION_SIZE,
	DEGREE_FIELD,
	DEGREE_NAME,
	DISABILITY_ENUM,
	GENDER,
	JOB_TYPE,
	LEVEL,
	MARITAL_STATUS,
	WORK_ENVIRONMENT,
} from "./enums"
import { z } from "zod"

export const organizationSignupSchema = z
	.object({
		_token: turnstile_token(),
		name: name(),
		username: username(),
		holder: name(),
		email: email(),
		password: password(),
		confirmPassword: password(),
		phone: phone(),
		neighborhood: neighborhood(),
		size: organizationSize(),
		sector: organizationSector(),
		certified: bool(),
		city: city(),
		logo: requiredImage,
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "@q:password_mismatch",
		path: ["confirmPassword"],
	})

export const employeeSignupSchema = z
	.object({
		_token: z.string().min(1),
		hasDisability: bool().optional(),
		name: name(),
		username: username(),
		phone: phone(),
		email: email(),
		age: age(),
		password: password(),
		confirmPassword: password(),
		neighborhood: neighborhood(),
		city: city(),
		gender: gender(false),
		maritalStatus: maritalStatus(),
		isSaudi: bool(),
		hearAbout: hearAbout(),
		hasCarLicense: bool(),
		disability: disability(),
		disabilityExtra: z.string().max(128).optional(),
		arrangements: z.string().min(1),
		jobType: jobType(),
		englishLevel: level(),
		arabicLevel: level(),
		computerSkills: level(),
		degree: degreeName(),
		field: degreeField(),
		previousExperience: previousExperience(),
		workEnvironment: z.array(z.enum(WORK_ENVIRONMENT), {
			error: "@q:invalid_work_environment",
		}),
		specialty: z.string().optional(),
		disabilityScene: requiredImage,
		cv: requiredImage,
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "@q:password_mismatch",
		path: ["confirmPassword"],
	})

export const loginSchema = z.object({
	_token: z.string().min(1),
	emailOrUsername: z
		.string()
		.trim()
		.toLowerCase()
		.min(1, "@q:invalid_email_or_username"),
	password: z.string().min(8, "@q:invalid_password"),
})

export type LoginFormData = z.infer<typeof loginSchema>

export const forgetPasswordSchema = z.object({
	_token: z.string().min(1),
	email: email(),
})

export const resetPasswordSchema = z
	.object({
		token: z.string().min(1, "@q:invalid_token"),
		password: password(),
		confirmPassword: password(),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "@q:password_mismatch",
		path: ["confirmPassword"],
	})

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export type ForgetPasswordFormData = z.infer<typeof forgetPasswordSchema>

export const createStaffSchema = z.object({
	name: name(),
	phone: phone(),
	username: username(),
	password: password(),
	emails: z.array(email()).default([]),
})

export type CreateStaffFormData = z.infer<typeof createStaffSchema>

export const editStaffSchema = z.object({
	name: name(),
	phone: phone(),
	username: username(),
	city: city(),
	neighborhood: neighborhood(),
})

export const newsletterSchema = z.object({
	email: email(),
})

export type NewsletterFormData = z.infer<typeof newsletterSchema>

export type EditStaffFormData = z.infer<typeof editStaffSchema>
